import type { SessionKind, WorkStep } from "@studio/shared";

/**
 * 🚨 v1 은 한 브라우저에 세션을 **한 칸**만 뒀다. 같은 PC 로 음악 수업과 포스터 수업에 차례로
 *    들어가면 두 번째가 첫 번째를 덮어써, 첫 탭이 남의 토큰으로 요청하다 403 을 받았다
 *    (2026-10-12 용인신촌중 prod 실측 — 학생 셋이 15:26 음악 · 15:55 포스터 입장 뒤 403 12건).
 *    시연작 둘을 띄워도 같았고, 시연 끝내기가 그 한 칸을 통째로 지워 다른 탭까지 끊었다.
 *
 * 🔑 그래서 v2 는 **작품마다 한 칸**이다. 어느 칸을 쓸지는 탭이 정한다 — 주소의
 *    `/(music|mv|pic|poster)/w/:workId` 가 1순위, 주소에 작품이 없으면 그 탭의 sessionStorage
 *    (탭마다 따로 산다), 그것도 없으면 가장 최근에 저장된 칸(= v1 의 «한 칸» 과 같은 동작).
 */
const legacySessionKey = "mvs:student-session:v1";
/**
 * 🚨 작품마다 **키를 따로** 둔다(작품 전부를 JSON 한 덩어리로 두지 않는다). 한 덩어리면 탭 둘이
 *    읽고-고치고-쓰기를 겹쳐 할 때 나중에 쓴 쪽이 다른 작품 칸을 지운다(Codex 검수 지적).
 *    키가 다르면 서로 다른 작품의 저장은 아예 같은 값을 건드리지 않는다.
 */
const sessionKeyPrefix = "mvs:student-session:v2:";
/** 가장 최근에 저장된 작품 id. 진입 화면 자동 이어하기가 쓴다. 덮어써져도 «어느 것이 최근인가» 만 흔들린다. */
const latestWorkKey = "mvs:student-session:v2-latest";
const activeWorkKey = "mvs:active-work";

/**
 * 학생 세션이 사라진 «사유» 를 남기는 자리.
 *
 * 🚨 B3(2026-08-12 prod)에서 우리가 못 가른 것이 정확히 이것이다. 학생 화면에 남은 단서는
 *    서버의 「인증이 필요합니다」 하나뿐인데, 그 문구는 서로 **조치가 다른 원인 셋**을
 *    구분하지 못한다(`server/src/auth/middleware.ts` — 헤더 없음 · Bearer 아님 · 토큰 파손).
 *    그래서 #79·#91 두 번의 수정이 나갔는데도 재현 조건은 아직 미확정이다.
 *
 * 🔑 그래서 이 파일은 «세 번째 가설» 을 세우지 않는다. 지우는 쪽이 **왜 지웠는지**를 적어 두고,
 *    다음 발생 때 화면이 스스로 원인을 말하게 한다(마스터 CLAUDE.md 디버깅 정책 —
 *    가설 2회 실패 시 진단 로그 우선).
 *
 * 🚨 이 값은 **세션이 지워진 뒤에도 남아야** 쓸모가 있다. 그래서 세션과 다른 키에 적는다.
 */
const lossKey = "mvs:session-loss:v1";

export type SessionLossReason =
  /** 저장분의 만료 시각이 지났다(= 토큰의 `exp`). 🚨 기기 시계와의 비교라 시계가 빠르면 여기로 온다. */
  | "expired"
  /** 저장분 모양이 계약과 다르다 — 배포로 필드가 바뀐 뒤 옛 저장분이 남아 있을 때. */
  | "shape"
  /** 저장분이 JSON 으로 안 읽힌다(잘렸거나 다른 것이 덮어썼다). */
  | "parse"
  /** 진입 화면 자동 이어하기에서 `/api/me` 가 410 이 아닌 이유로 실패했다 — 네트워크 끊김·배포 중 5xx 포함. */
  | "me-failed"
  /** 학생이 나가기를 눌러 확인했다. */
  | "exit"
  /** 수업이 끝나 참여 화면으로 되돌아갔다. */
  | "ended"
  /** 참여가 풀려 코드 다시 넣기로 갔다. */
  | "rejoin"
  /** 교사가 시연을 접었다. */
  | "demo-exit";

export interface SessionLossBreadcrumb {
  reason: SessionLossReason;
  /** 지운 시각(기기 시계). 🔑 `expired` 일 때 만료 시각과 나란히 보면 시계 어긋남이 드러난다. */
  at: number;
  detail?: string;
}

/**
 * 사유를 남기고 같은 내용을 콘솔에도 찍는다.
 *
 * 🚨 저장에 실패해도 **삭제를 막지 않는다** — 진단이 제품 동작을 바꾸면 그건 진단이 아니다.
 */
/**
 * 사람이 눌러서 일어난 «설명되는» 손실. 이것들은 사고가 아니라 정상 동작이다.
 *
 * 🚨 이 구분이 없으면 두 가지가 동시에 망가진다:
 *   1) 교사가 시연을 끝내는 것 같은 **정상 조작이 console.error 를 찍는다** —
 *      「콘솔이 깨끗한가」를 보는 검사가 빨개진다(2026-08-13 prod-ui b1-demo-console-clean 실측).
 *   2) 더 나쁜 쪽 — 진단의 값이 떨어진다. 콘솔에 error 가 상시로 깔려 있으면
 *      정작 «설명 안 되는 손실» 이 그 사이에 묻힌다. B3 에서 찾으려는 것이 바로 그것이다.
 */
const explainedReasons: ReadonlySet<SessionLossReason> = new Set(["exit", "ended", "rejoin", "demo-exit"]);

export function recordSessionLoss(reason: SessionLossReason, detail?: string): void {
  const crumb: SessionLossBreadcrumb = { reason, at: Date.now(), ...(detail ? { detail } : {}) };
  // 🔑 사람이 개발자도구를 열었을 때 바로 보이는 것이 첫 번째 회수 경로다.
  //    🚨 다만 **error 는 «설명 안 되는» 손실에만** 쓴다 — 정상 조작까지 error 로 찍으면
  //       찾으려는 신호가 자기 소음에 묻힌다. 기록(브레드크럼)은 어느 쪽이든 똑같이 남는다.
  const line = `[mvs:session-loss] ${reason}${detail ? ` — ${detail}` : ""}`;
  if (explainedReasons.has(reason)) console.info(line);
  else console.error(line);
  try {
    localStorage.setItem(lossKey, JSON.stringify(crumb));
  } catch {
    // 저장 못 해도 콘솔에는 남았다.
  }
}

/** 마지막으로 기록된 손실 사유. 없으면 null. */
export function readSessionLoss(): SessionLossBreadcrumb | null {
  try {
    const raw = localStorage.getItem(lossKey);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const candidate = parsed as Partial<SessionLossBreadcrumb>;
    if (typeof candidate.reason !== "string" || typeof candidate.at !== "number") return null;
    return candidate as SessionLossBreadcrumb;
  } catch {
    return null;
  }
}

/**
 * 토큰을 읽지 못했을 때만 쓰는 보수적 기본값.
 *
 * 🚨 서버가 학생 토큰을 **발급 시점 12시간 고정**으로 낸다(`server/src/auth/student.ts`
 *    `setExpirationTime("12h")`). 갱신도 연장도 없다. 그런데 종전 이 파일은 **24시간**을
 *    쓰면서 `updateSession` 마다 만료를 **다시 연장**했다 — 로컬은 «살아 있다» 고 믿는데
 *    서버는 거절하는 구간이 생긴다. (#79 가 그 401 을 받아내 「다시 참여해 주세요」를
 *    띄우지만, 어긋남 자체는 남아 있었다 — 핸드오프 §6.)
 *
 * 그래서 이제 **토큰이 스스로 말하는 만료(`exp`)** 를 쓴다. 둘이 어긋날 수 없는 값이
 * 하나뿐이면 어긋남도 없다. 이 상수는 토큰을 못 읽는 예외에서만 쓰이고, 그때도
 * **서버보다 길지 않게** 잡는다.
 */
const fallbackSessionTtlMs = 12 * 60 * 60 * 1_000;

/**
 * 학생 토큰(JWT)이 말하는 만료 시각(ms). 서명은 검증하지 않는다 — 여기서 필요한 것은
 * «언제까지 유효하다고 서버가 적어 놨는가» 뿐이고, 진짜 판정은 서버가 매 요청에서 한다.
 */
function expiryFromToken(token: string): number | null {
  try {
    const encoded = token.split(".")[1];
    if (!encoded) return null;
    const payload: unknown = JSON.parse(atob(encoded.replace(/-/gu, "+").replace(/_/gu, "/")));
    const exp = (payload as { exp?: unknown }).exp;
    return typeof exp === "number" && Number.isFinite(exp) ? exp * 1_000 : null;
  } catch {
    return null;
  }
}

export interface StudentSession {
  token: string;
  workId: string;
  name: string;
  code: string;
  hummingEnabled: boolean;
  kind: SessionKind;
  expiresAt: number;
  activeJobId: string | null;
  cancelRequested: boolean;
  /**
   * 시연작(§9.H-14 B형) 진행 중인가. 교사가 자기 데모 세션에 참여자로 들어온 상태다.
   * 학생 화면은 **한 줄도 달라지지 않는다** — 이 값은 화면 위에 교사 바를 얹을지만 정한다.
   */
  demo: boolean;
  /** 교사 바에서 모니터링·종료로 갈 때 쓰는 데모 세션 id. */
  demoSessionId: string | null;
}

function validSession(value: unknown): value is StudentSession {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<StudentSession>;
  return (
    typeof candidate.token === "string" &&
    typeof candidate.workId === "string" &&
    typeof candidate.name === "string" &&
    typeof candidate.code === "string" &&
    typeof candidate.hummingEnabled === "boolean" &&
    // 🚨 수업 종류가 늘면 여기도 늘려야 한다 — 모르는 종류는 «손상된 저장분» 으로 지워진다.
    //    그림 수업(2026-09-24)을 빠뜨리면 학생이 이름을 넣자마자 「다시 참여해 주세요」로 튕긴다.
    (candidate.kind === undefined || candidate.kind === "music" || candidate.kind === "mv"
      || candidate.kind === "image" || candidate.kind === "poster") &&
    typeof candidate.expiresAt === "number" &&
    (typeof candidate.activeJobId === "string" || candidate.activeJobId === null) &&
    typeof candidate.cancelRequested === "boolean" &&
    (candidate.demo === undefined || typeof candidate.demo === "boolean") &&
    (candidate.demoSessionId === undefined
      || candidate.demoSessionId === null
      || typeof candidate.demoSessionId === "string")
  );
}

type StoredSession = StudentSession & { savedAt?: number };

function entryKey(workId: string): string {
  return `${sessionKeyPrefix}${workId}`;
}

/**
 * v1 한 칸. 🚨 **지우지 않고 읽기만** 한다 — 배포 직후에는 옛 코드로 떠 있는 학생 탭이 아직
 * v1 만 읽는다. 새 코드가 v1 을 지우면 그 탭이 다음 요청에서 토큰을 잃는다(Codex 검수 지적).
 * v1 은 그 작품을 나가거나 만료될 때(`removeEntry`) 함께 지운다.
 */
function legacySession(): StudentSession | null {
  let raw: string | null;
  try {
    raw = localStorage.getItem(legacySessionKey);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (validSession(parsed)) return parsed;
    // 옛 코드도 이 저장분은 버린다 — 지워도 옛 탭이 잃는 것이 없다.
    recordSessionLoss("shape", "v1 저장분이 StudentSession 계약과 다르다");
  } catch (error) {
    recordSessionLoss("parse", `v1 저장분을 읽지 못했다 — ${error instanceof Error ? error.name : "unknown"}`);
  }
  localStorage.removeItem(legacySessionKey);
  return null;
}

/**
 * 그 작품의 저장분(가공 전 문자열). v2 칸이 없고 v1 이 그 작품이면 v1 을 v2 로 **복사**하고 v1 을 돌려준다.
 * 🔑 복사가 실패해도(저장 공간 초과 등) v1 이 그대로 남아 있어 다음에 다시 옮긴다 — 옮기다 잃지 않는다.
 */
function entryRaw(workId: string): string | null {
  const raw = localStorage.getItem(entryKey(workId));
  if (raw !== null) return raw;
  const legacy = legacySession();
  if (!legacy || legacy.workId !== workId) return null;
  const copied = JSON.stringify({ ...legacy, savedAt: 0 } satisfies StoredSession);
  try {
    localStorage.setItem(entryKey(workId), copied);
    if (localStorage.getItem(latestWorkKey) === null) localStorage.setItem(latestWorkKey, workId);
  } catch {
    // v1 이 남아 있으니 다음 읽기에서 다시 시도한다.
  }
  return copied;
}

/** 그 작품 칸과(같은 작품이면) v1 칸을 지운다. 다른 작품 칸에는 손대지 않는다. */
function removeEntry(workId: string): void {
  localStorage.removeItem(entryKey(workId));
  if (legacySession()?.workId === workId) localStorage.removeItem(legacySessionKey);
  if (localStorage.getItem(latestWorkKey) === workId) localStorage.removeItem(latestWorkKey);
  const tab = sessionStorageOrNull();
  try {
    if (tab?.getItem(activeWorkKey) === workId) tab.removeItem(activeWorkKey);
  } catch {
    // 탭 기억은 주소가 바뀌면 다시 잡힌다.
  }
}

/** 저장된 v2 칸의 작품 id 전부. 저장소가 키 목록을 못 주면(시험용 대역) 빈 목록. */
function storedWorkIds(): string[] {
  const ids: string[] = [];
  try {
    if (typeof localStorage.key !== "function") return ids;
    for (let index = 0; index < localStorage.length; index += 1) {
      const key = localStorage.key(index);
      if (key?.startsWith(sessionKeyPrefix)) ids.push(key.slice(sessionKeyPrefix.length));
    }
  } catch {
    // 목록을 못 읽으면 최근 표시(latestWorkKey)만으로 고른다.
  }
  return ids;
}

function sessionStorageOrNull(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

const workPathPattern = /^\/(?:music|mv|pic|poster)\/w\/([^/]+)/u;

/** 주소가 가리키는 작품 id. 작품 화면이 아니면 null. */
function pathWorkId(): string | null {
  const pathname = typeof window === "undefined" ? "" : window.location?.pathname ?? "";
  const match = workPathPattern.exec(pathname);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

/**
 * 이 탭이 지금 보고 있는 작품. 주소가 1순위이고, 주소에 작품이 없으면 이 탭이 마지막으로 본 작품.
 * 가장 최근 칸으로는 **떨어지지 않는다** — 교사 바처럼 «이 탭의 것» 만 보여야 하는 곳이 쓴다.
 */
export function currentWorkId(): string | null {
  const tab = sessionStorageOrNull();
  const fromPath = pathWorkId();
  try {
    if (fromPath) {
      tab?.setItem(activeWorkKey, fromPath);
      return fromPath;
    }
    return tab?.getItem(activeWorkKey) ?? null;
  } catch {
    // 탭 기억을 못 해도 주소가 있는 동안은 맞게 고른다.
    return fromPath;
  }
}

function savedAtOf(workId: string): number {
  try {
    const parsed = JSON.parse(localStorage.getItem(entryKey(workId)) ?? "null") as StoredSession | null;
    return typeof parsed?.savedAt === "number" ? parsed.savedAt : 0;
  } catch {
    return 0;
  }
}

/** 가장 최근에 저장된 작품. 최근 표시 → 칸 목록의 savedAt → v1 순서로 찾는다. */
function latestWorkId(): string | null {
  const marked = localStorage.getItem(latestWorkKey);
  if (marked && entryRaw(marked) !== null) return marked;
  let latest: string | null = null;
  let latestAt = -Infinity;
  for (const workId of storedWorkIds()) {
    const at = savedAtOf(workId);
    if (at > latestAt) {
      latest = workId;
      latestAt = at;
    }
  }
  return latest ?? legacySession()?.workId ?? null;
}

/**
 * 🚨 주소가 작품을 가리키는데 그 칸이 없으면 **남의 칸으로 떨어지지 않는다** — 떨어지면
 *    v1 과 똑같이 남의 토큰으로 요청해 403 을 받는다. 그때는 「참여가 풀렸다」 가 맞는 안내다.
 */
function resolveWorkId(workId?: string): string | null {
  if (workId) return workId;
  const current = currentWorkId();
  if (current && (pathWorkId() === current || entryRaw(current) !== null)) return current;
  return latestWorkId();
}

/**
 * `workId` 를 주면 그 작품의 세션, 안 주면 이 탭이 보고 있는 작품의 세션.
 *
 * 🚨 만료·손상은 **그 작품 칸만** 지운다. 다른 수업의 칸에는 손대지 않는다.
 */
export function loadSession(workId?: string): StudentSession | null {
  const id = resolveWorkId(workId);
  if (!id) return null;
  let entry: unknown;
  try {
    const raw = entryRaw(id);
    if (raw === null) return null;
    entry = JSON.parse(raw);
  } catch (error) {
    recordSessionLoss("parse", `저장분을 읽지 못했다 — ${error instanceof Error ? error.name : "unknown"}`);
    removeEntry(id);
    return null;
  }
  // 🚨 종전에는 «모양이 다르다» 와 «만료됐다» 를 한 줄에서 함께 지웠다. 조치가 다른 둘인데
  //    흔적이 같아서, 사고가 나도 어느 쪽이었는지 되짚을 수 없었다(B3).
  if (!validSession(entry) || entry.workId !== id) {
    recordSessionLoss("shape", "저장분이 StudentSession 계약과 다르다");
    removeEntry(id);
    return null;
  }
  if (entry.expiresAt <= Date.now()) {
    // 🔑 두 시각을 같이 적는다. 만료가 «지금» 을 방금 지난 것인지, 기기 시계가 서버보다
    //    앞서서 처음부터 지나 있었던 것인지는 이 차이로만 갈린다(#91 이 만든 노출).
    const overdueSec = Math.round((Date.now() - entry.expiresAt) / 1_000);
    recordSessionLoss("expired", `만료 ${overdueSec}초 지남 · exp=${entry.expiresAt} · 기기시각=${Date.now()}`);
    removeEntry(id);
    return null;
  }
  const { savedAt: _savedAt, ...session } = entry as StoredSession;
  return {
    ...session,
    kind: session.kind ?? "music",
    demo: session.demo ?? false,
    demoSessionId: session.demoSessionId ?? null,
  };
}

/**
 * 진입 화면(`/`·`/join`) 자동 이어하기용 — **가장 최근에 저장된** 작품의 세션(핸드오프 PR1).
 * 🔑 고른 작품을 이 탭의 기억에도 적는다. 그래야 뒤따르는 `/api/me` 가 같은 작품의 토큰을 단다 —
 *    고른 세션과 요청에 실린 토큰이 다르면 남의 작품 단계로 이동한다.
 */
export function loadLatestSession(): StudentSession | null {
  const id = latestWorkId();
  if (!id) return null;
  try {
    sessionStorageOrNull()?.setItem(activeWorkKey, id);
  } catch {
    // 기억을 못 적으면 요청도 가장 최근 칸으로 떨어진다 — 같은 작품이다.
  }
  return loadSession(id);
}

export function saveSession(
  input:
    & Omit<StudentSession, "expiresAt" | "activeJobId" | "cancelRequested" | "demo" | "demoSessionId">
    & Partial<Pick<StudentSession, "demo" | "demoSessionId">>,
): StudentSession {
  const session: StudentSession = {
    ...input,
    demo: input.demo ?? false,
    demoSessionId: input.demoSessionId ?? null,
    expiresAt: expiryFromToken(input.token) ?? Date.now() + fallbackSessionTtlMs,
    activeJobId: null,
    cancelRequested: false,
  };
  const now = Date.now();
  // 🚨 다른 작품의 만료 칸을 여기서 걷어내지 않는다. 읽고-지우기 사이에 다른 탭이 그 작품에
  //    다시 들어와 새 토큰을 저장하면, 살아 있는 세션을 지우게 된다(Codex 재검수 지적).
  //    만료 칸은 그 작품을 열 때(loadSession) 사유와 함께 지워진다. 칸 하나는 수백 바이트라 쌓여도 작다.
  localStorage.setItem(entryKey(session.workId), JSON.stringify({ ...session, savedAt: now } satisfies StoredSession));
  localStorage.setItem(latestWorkKey, session.workId);
  try {
    sessionStorageOrNull()?.setItem(activeWorkKey, session.workId);
  } catch {
    // 이 탭이 곧 그 작품 주소로 가므로 주소가 대신 골라 준다.
  }
  return session;
}

export function updateSession(
  update: Partial<Pick<StudentSession,
    "activeJobId" | "cancelRequested" | "name" | "hummingEnabled" | "kind"
  >>,
  workId?: string,
): StudentSession | null {
  const current = loadSession(workId);
  if (!current) return null;
  // 🚨 만료를 **연장하지 않는다**. 서버 토큰은 발급 시점 12시간 고정이라 로컬만 늘리면
  //    「로컬은 살아 있다고 믿는데 서버는 거절하는」 구간이 커질 뿐이다.
  const next = { ...current, ...update };
  // 🔑 이 작품의 키 하나만 쓴다 — 다른 탭이 다른 작품을 저장하는 것과 겹쳐도 서로 지우지 않는다.
  try {
    localStorage.setItem(entryKey(current.workId), JSON.stringify({ ...next, savedAt: savedAtOf(current.workId) } satisfies StoredSession));
  } catch (error) {
    // 🚨 던지지 않는다. 갱신하는 값(진행 중 작업 id 등)은 서버가 /api/me 로 다시 알려 준다.
    //    던지면 부르는 쪽이 «/api/me 실패» 로 착각해 세션을 통째로 지운다(Codex 재검수 지적 — 저장 공간 초과).
    console.error(`[mvs:session-update-failed] ${current.workId} — ${error instanceof Error ? error.name : "unknown"}`);
  }
  return next;
}

/**
 * 🚨 사유를 **인자로 받는다**(선택이 아니다). 종전에는 다섯 자리에서 아무 표시 없이 지웠고,
 *    그래서 「학생 화면은 그대로인데 토큰만 없다」는 상태의 출처를 아무도 되짚을 수 없었다.
 *
 * 🚨 **그 작품 하나만** 지운다(`workId` 생략 = 이 탭이 보고 있는 작품). 종전에는 한 칸을 통째로
 *    지워 시연 하나를 끝내면 다른 탭의 시연·학생 화면까지 끊겼다(2026-10-12).
 */
export function clearSession(reason: SessionLossReason, detail?: string, workId?: string): void {
  recordSessionLoss(reason, detail);
  const id = resolveWorkId(workId);
  if (id) removeEntry(id);
}

/** 요청 경로가 가리키는 작품 id. `/api/works/:id…` 가 아니면 null. */
export function workIdFromApiPath(path: string): string | null {
  const match = /^\/api\/works\/([^/?#]+)/u.exec(path);
  if (!match?.[1]) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export function pathForStep(workId: string, step: WorkStep): string {
  const suffix: Record<WorkStep, string> = {
    mode: "",
    lyrics: "/lyrics",
    humming: "/humming",
    style: "/style",
    result: "/result",
    done: "/done",
    song: "",
    story: "/story",
    cuts: "/cuts",
    mv_done: "/done",
    edit: "/edit",
    final: "/final",
    pic_pick: "",
    pic_image: "/image",
    pic_song: "/song",
    pic_done: "/done",
    pst_draw: "",
    pst_edit: "/edit",
    pst_song: "/song",
    pst_done: "/confirm",
  };
  const root = step === "song" || step === "story" || step === "cuts" || step === "mv_done"
    || step === "edit" || step === "final"
    ? "mv"
    : step.startsWith("pic_")
      ? "pic"
      : step.startsWith("pst_")
        ? "poster"
        : "music";
  return `/${root}/w/${encodeURIComponent(workId)}${suffix[step]}`;
}
